import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { levels } from './levels';
import aiLevels from './levels-ai';
import { runPythonCode } from './pyodideRunner';
import { setupPythonMockAPI } from './utils/ai-api-mock';
import LearningView from './components/LearningView';
import PracticeMode from './components/PracticeMode';
import LandingPage from './components/LandingPage';
import GoldenLearnDashboard from './components/GoldenLearnDashboard';
import GoldenAIDashboard from './components/GoldenAIDashboard';
import GoldenAIWelcome from './components/GoldenAIWelcome';
import GoldenQuizWelcome from './components/GoldenQuizWelcome';
import GoldenQuizDashboard from './components/GoldenQuizDashboard';
import QuizPlayer from './components/QuizPlayer';
import QuizResults from './components/QuizResults';
import GoldenStartupWelcome from './components/GoldenStartupWelcome';
import GoldenCommonsWelcome from './components/GoldenCommonsWelcome';
import OfflineIndicator from './components/OfflineIndicator';
import CompletionRitual from './components/CompletionRitual';
import LearningCircle from './components/LearningCircle';
import { getStory } from './stories';
import { storiesAI } from './stories-ai';
import { getPillar } from './pillars';

function App() {
  const [currentLevel, setCurrentLevel] = useState(0);
  const [code, setCode] = useState('');
  const [output, setOutput] = useState('');
  const [feedback, setFeedback] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [completed, setCompleted] = useState(() => {
    const saved = localStorage.getItem('sifiso-completed');
    return saved ? JSON.parse(saved) : Array(levels.length).fill(false);
  });
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(() => {
    const saved = localStorage.getItem('sifiso-phase');
    return saved ? JSON.parse(saved) : Array(levels.length).fill('learning');
  });
  const [currentPhase, setCurrentPhase] = useState('learning');
  const [isPyodideLoading, setIsPyodideLoading] = useState(false);
  const [pyodideLoadingMessage, setPyodideLoadingMessage] = useState('');
  const [pyodideError, setPyodideError] = useState(null);
  const [isPracticeMode, setIsPracticeMode] = useState(false);
  const [showLearningCircle, setShowLearningCircle] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(() => {
    // Check if user has previously entered the platform
    const hasStarted = localStorage.getItem('sifiso-has-started');
    return !hasStarted; // Show landing page if user hasn't started yet
  });
  const [showGoldenLearnDashboard, setShowGoldenLearnDashboard] = useState(false);
  const [showGoldenAIWelcome, setShowGoldenAIWelcome] = useState(false);
  const [showGoldenAIDashboard, setShowGoldenAIDashboard] = useState(false);
  const [showGoldenQuizWelcome, setShowGoldenQuizWelcome] = useState(false);
  const [showGoldenQuizDashboard, setShowGoldenQuizDashboard] = useState(false);
  const [showQuizPlayer, setShowQuizPlayer] = useState(false);
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [currentQuizCategory, setCurrentQuizCategory] = useState(null);
  const [currentQuizDifficulty, setCurrentQuizDifficulty] = useState(null);
  const [quizTimedMode, setQuizTimedMode] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [showGoldenStartupWelcome, setShowGoldenStartupWelcome] = useState(false);
  const [showGoldenCommonsWelcome, setShowGoldenCommonsWelcome] = useState(false);
  const [showCompletionRitual, setShowCompletionRitual] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);

  // Golden AI specific state
  const [isAIMode, setIsAIMode] = useState(false);
  const [currentAILevel, setCurrentAILevel] = useState(0);
  const [completedAI, setCompletedAI] = useState(() => {
    const saved = localStorage.getItem('sifiso-ai-completed');
    return saved ? JSON.parse(saved) : Array(aiLevels.length).fill(false);
  });
  const [phaseAI, setPhaseAI] = useState(() => {
    const saved = localStorage.getItem('sifiso-ai-phase');
    return saved ? JSON.parse(saved) : Array(aiLevels.length).fill('learning');
  });
  const [currentAIPhase, setCurrentAIPhase] = useState('learning');

  const level = isAIMode ? aiLevels[currentAILevel] : levels[currentLevel];

  // Load completed progress and calculate initial progress
  useEffect(() => {
    const saved = localStorage.getItem('sifiso-completed');
    if (saved) {
      const comp = JSON.parse(saved);
      setCompleted(comp);
      setProgress(comp.filter(c => c).length);
    }
  }, []);

  // Update code when level changes
  useEffect(() => {
    setCode(level.starterCode || '');
    setOutput('');
    setFeedback('');
    setShowHint(false);
    setShowExplanation(false);
    if (isAIMode) {
      setCurrentAIPhase(phaseAI[currentAILevel]);
    } else {
      setCurrentPhase(phase[currentLevel]);
    }
  }, [currentLevel, currentAILevel, phase, phaseAI, isAIMode]);

  const handleRun = async () => {
    setFeedback('Running...');
    setOutput('');
    setPyodideError(null);

    const testInputs = level.tests.flatMap(t =>
      t.input ? t.input.split('\n').map(line => line.trim()).filter(Boolean) : []
    );

    // Show loading modal if Pyodide needs to be loaded
    const { isPyodideLoaded } = await import('./pyodideRunner');
    if (!isPyodideLoaded()) {
      setIsPyodideLoading(true);
      setPyodideLoadingMessage('Initializing Python environment...');
    }

    try {
      const out = await runPythonCode(code, testInputs, (message) => {
        setPyodideLoadingMessage(message);
      }, isAIMode ? setupPythonMockAPI : null);

      setOutput(out);
      checkCorrect(out);
    } catch (error) {
      console.error('Pyodide error:', error);
      setPyodideError(error.message || 'Failed to load Python environment. Please refresh and try again.');
      setFeedback('❌ Error loading Python');
      setOutput('');
    } finally {
      setIsPyodideLoading(false);
    }
  };

  const checkCorrect = (out) => {
    let allPassed = true;
    let normalizedOut = out.trim();

    for (const test of level.tests) {
      let testNormalized = normalizedOut;

      // Remove prompt if specified
      if (test.prompt) {
        const promptLine = test.prompt.trim();
        if (testNormalized.startsWith(promptLine)) {
          testNormalized = testNormalized.slice(promptLine.length).trimStart();
        }
      }

      // Remove echoed first input line (non-tty behavior)
      if (test.input) {
        const inputLines = test.input.split('\n').map(l => l.trim()).filter(Boolean);
        if (inputLines.length > 0) {
          const firstInput = inputLines[0];
          if (testNormalized.startsWith(firstInput)) {
            testNormalized = testNormalized.slice(firstInput.length).trimStart();
          }
        }
      }

      let expected = test.expected;
      if (typeof expected === 'string') {
        expected = expected.trim();
      }

      let passed = false;
      if (typeof expected === 'string') {
        passed = testNormalized === expected;
      } else if (expected instanceof RegExp) {
        passed = expected.test(testNormalized);
      }

      if (!passed) {
        allPassed = false;
      }
    }

    if (allPassed) {
      // Mark level as completed if not already
      if (isAIMode) {
        if (!completedAI[currentAILevel]) {
          const newCompleted = [...completedAI];
          newCompleted[currentAILevel] = true;
          setCompletedAI(newCompleted);
          localStorage.setItem('sifiso-ai-completed', JSON.stringify(newCompleted));
        }
      } else {
        if (!completed[currentLevel]) {
          const newCompleted = [...completed];
          newCompleted[currentLevel] = true;
          setCompleted(newCompleted);
          setProgress(newCompleted.filter(c => c).length);
          localStorage.setItem('sifiso-completed', JSON.stringify(newCompleted));
        }
      }

      // Show completion ritual - pause the learner
      setShowCompletionRitual(true);
    } else {
      setFeedback('❌ Incorrect. Try again.');
      setShowExplanation(true); // Show explanation on failure for learning
    }
  };

  const resetLevel = () => {
    setCode(level.starterCode || '');
    setOutput('');
    setFeedback('');
    setShowHint(false);
    setShowExplanation(false);
  };

  const handleStartCoding = () => {
    if (isAIMode) {
      const newPhase = [...phaseAI];
      newPhase[currentAILevel] = 'coding';
      setPhaseAI(newPhase);
      setCurrentAIPhase('coding');
      localStorage.setItem('sifiso-ai-phase', JSON.stringify(newPhase));
    } else {
      const newPhase = [...phase];
      newPhase[currentLevel] = 'coding';
      setPhase(newPhase);
      setCurrentPhase('coding');
      localStorage.setItem('sifiso-phase', JSON.stringify(newPhase));
    }
  };

  const handleBackToLearning = () => {
    if (isAIMode) {
      setCurrentAIPhase('learning');
    } else {
      setCurrentPhase('learning');
    }
  };

  const handleLevelChange = (levelIndex) => {
    setCurrentLevel(levelIndex);
  };

  const handleMarkLevelComplete = () => {
    if (!completed[currentLevel]) {
      const newCompleted = [...completed];
      newCompleted[currentLevel] = true;
      setCompleted(newCompleted);
      setProgress(newCompleted.filter(c => c).length);
      localStorage.setItem('sifiso-completed', JSON.stringify(newCompleted));
    }
  };

  const handleEnterGoldenLearn = () => {
    setShowLandingPage(false);
    setShowGoldenLearnDashboard(true);
    localStorage.setItem('sifiso-has-started', 'true');
  };

  const handleEnterGoldenCommons = () => {
    setShowLandingPage(false);
    setShowGoldenCommonsWelcome(true);
    localStorage.setItem('sifiso-has-started', 'true');
  };

  const handleEnterGoldenAI = () => {
    setShowLandingPage(false);
    setShowGoldenAIWelcome(true);
    localStorage.setItem('sifiso-has-started', 'true');
  };

  const handleEnterGoldenAIDashboard = () => {
    setShowGoldenAIWelcome(false);
    setShowGoldenAIDashboard(true);
  };

  const handleAIDashboardSelectLevel = (levelIndex) => {
    setCurrentAILevel(levelIndex);
    setShowGoldenAIDashboard(false);
    setIsAIMode(true);
    setCurrentAIPhase(phaseAI[levelIndex]);
  };

  const handleEnterGoldenQuiz = () => {
    setShowLandingPage(false);
    setShowGoldenQuizWelcome(true);
    localStorage.setItem('sifiso-has-started', 'true');
  };

  const handleEnterGoldenQuizDashboard = () => {
    setShowGoldenQuizWelcome(false);
    setShowGoldenQuizDashboard(true);
  };

  const handleQuizStart = (categoryId, difficulty, timedMode = false) => {
    setCurrentQuizCategory(categoryId);
    setCurrentQuizDifficulty(difficulty);
    setQuizTimedMode(timedMode);
    setShowGoldenQuizDashboard(false);
    setShowQuizPlayer(true);
  };

  const handleQuizComplete = (results) => {
    setQuizResults(results);
    setShowQuizPlayer(false);
    setShowQuizResults(true);
  };

  const handleQuizRetry = () => {
    setShowQuizResults(false);
    setShowQuizPlayer(true);
  };

  const handleQuizExit = () => {
    setShowQuizPlayer(false);
    setShowQuizResults(false);
    setShowGoldenQuizDashboard(true);
    setCurrentQuizCategory(null);
    setCurrentQuizDifficulty(null);
    setQuizResults(null);
  };

  const handleEnterGoldenStartup = () => {
    setShowLandingPage(false);
    setShowGoldenStartupWelcome(true);
    localStorage.setItem('sifiso-has-started', 'true');
  };

  const handleEnterLearningCircles = () => {
    setShowGoldenCommonsWelcome(false);
    setShowLearningCircle(true);
  };

  const handleDashboardSelectLevel = (levelIndex) => {
    setCurrentLevel(levelIndex);
    setShowGoldenLearnDashboard(false);
  };

  const handleDashboardStartLearning = () => {
    setCurrentLevel(0);
    setShowGoldenLearnDashboard(false);
  };

  const handleBackToSIFISO = () => {
    setShowLandingPage(true);
    setShowGoldenLearnDashboard(false);
    setShowGoldenAIWelcome(false);
    setShowGoldenAIDashboard(false);
    setShowGoldenQuizWelcome(false);
    setShowGoldenQuizDashboard(false);
    setShowQuizPlayer(false);
    setShowQuizResults(false);
    setShowGoldenStartupWelcome(false);
    setShowGoldenCommonsWelcome(false);
    setShowLearningCircle(false);
    setIsPracticeMode(false);
    setIsAIMode(false);
  };

  const handleCompletionContinue = () => {
    // Close completion ritual
    setShowCompletionRitual(false);
    // Reset for next level if needed
    setFeedback('');
    setOutput('');
    setShowExplanation(false);
  };

  // Show landing page if user hasn't started yet
  if (showLandingPage) {
    return (
      <LandingPage
        onEnterGoldenLearn={handleEnterGoldenLearn}
        onEnterGoldenAI={handleEnterGoldenAI}
        onEnterGoldenQuiz={handleEnterGoldenQuiz}
        onEnterGoldenStartup={handleEnterGoldenStartup}
        onEnterGoldenCommons={handleEnterGoldenCommons}
      />
    );
  }

  // Show Golden Learn Dashboard
  if (showGoldenLearnDashboard) {
    return (
      <GoldenLearnDashboard
        levels={levels}
        completed={completed}
        currentLevel={currentLevel}
        onSelectLevel={handleDashboardSelectLevel}
        onStartLearning={handleDashboardStartLearning}
        onBackToSIFISO={handleBackToSIFISO}
        onEnterPracticeMode={() => {
          setShowGoldenLearnDashboard(false);
          setIsPracticeMode(true);
        }}
        onEnterLearningCircle={() => {
          setShowGoldenLearnDashboard(false);
          setShowLearningCircle(true);
        }}
      />
    );
  }

  // Show Golden AI Welcome
  if (showGoldenAIWelcome) {
    return (
      <GoldenAIWelcome
        onBackToSIFISO={handleBackToSIFISO}
        onEnterGoldenAI={handleEnterGoldenAIDashboard}
      />
    );
  }

  // Show Golden AI Dashboard
  if (showGoldenAIDashboard) {
    return (
      <GoldenAIDashboard
        onBackToSIFISO={handleBackToSIFISO}
        onSelectLevel={handleAIDashboardSelectLevel}
      />
    );
  }

  // Show Golden Quiz Welcome
  if (showGoldenQuizWelcome) {
    return (
      <GoldenQuizWelcome
        onBackToSIFISO={handleBackToSIFISO}
        onEnterGoldenQuiz={handleEnterGoldenQuizDashboard}
      />
    );
  }

  // Show Golden Quiz Dashboard
  if (showGoldenQuizDashboard) {
    return (
      <GoldenQuizDashboard
        onBackToSIFISO={handleBackToSIFISO}
        onStartQuiz={handleQuizStart}
      />
    );
  }

  // Show Quiz Player
  if (showQuizPlayer) {
    return (
      <QuizPlayer
        categoryId={currentQuizCategory}
        difficulty={currentQuizDifficulty}
        timedMode={quizTimedMode}
        onComplete={handleQuizComplete}
        onExit={handleQuizExit}
      />
    );
  }

  // Show Quiz Results
  if (showQuizResults) {
    return (
      <QuizResults
        results={quizResults}
        onRetry={handleQuizRetry}
        onBackToDashboard={handleQuizExit}
      />
    );
  }

  // Show Golden Startup Welcome
  if (showGoldenStartupWelcome) {
    return (
      <GoldenStartupWelcome
        onBackToSIFISO={handleBackToSIFISO}
      />
    );
  }

  // Show Golden Commons Welcome
  if (showGoldenCommonsWelcome) {
    return (
      <GoldenCommonsWelcome
        onBackToSIFISO={handleBackToSIFISO}
        onEnterLearningCircles={handleEnterLearningCircles}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200">
      {/* Offline Indicator - Subtle, normalizes offline state */}
      <OfflineIndicator />

      {/* Pyodide Loading Modal */}
      {isPyodideLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-8 max-w-md mx-4 text-center">
            <div className="mb-4">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
            </div>
            <h3 className="text-xl font-bold mb-2">Loading Python Environment</h3>
            <p className="text-slate-300 mb-4">{pyodideLoadingMessage}</p>
            <div className="text-sm text-slate-400">
              <p>This only happens once on first run.</p>
              <p>Python is being downloaded to your browser.</p>
            </div>
          </div>
        </div>
      )}

      {/* Pyodide Error Modal */}
      {pyodideError && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-8 max-w-md mx-4">
            <h3 className="text-xl font-bold mb-4 text-red-400">Python Loading Failed</h3>
            <p className="text-slate-300 mb-4">{pyodideError}</p>
            <div className="text-sm text-slate-400 mb-6">
              <p className="mb-2">Possible solutions:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Check your internet connection</li>
                <li>Refresh the page and try again</li>
                <li>Clear browser cache</li>
                <li>Try a different browser</li>
              </ul>
            </div>
            <button
              onClick={() => {
                setPyodideError(null);
                window.location.reload();
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-semibold"
            >
              Refresh Page
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 p-4 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold">
              <span className="text-amber-400">SIFISO</span>{' '}
              <span className="text-2xl font-semibold italic" style={{ color: isAIMode ? '#D4AF37' : '#FFD700' }}>
                {isAIMode ? 'Golden AI' : 'Golden Learn'}
              </span>
            </h1>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowLandingPage(true)}
                className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 transition text-sm"
                title="Return to SIFISO"
              >
                🏠 SIFISO
              </button>
              <button
                onClick={() => isAIMode ? setShowGoldenAIDashboard(true) : setShowGoldenLearnDashboard(true)}
                className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 transition text-sm"
                title="Return to dashboard"
              >
                📊 Dashboard
              </button>
              <button
                onClick={() => setSidebarVisible(!sidebarVisible)}
                className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 transition text-sm"
                title={sidebarVisible ? 'Hide Sidebar' : 'Show Sidebar'}
              >
                {sidebarVisible ? '◀ Hide Levels' : '▶ Show Levels'}
              </button>
            </div>
          </div>
          {/* Pillar-segmented progress bar */}
          <div className="mt-2 max-w-2xl mx-auto bg-slate-800 rounded-full h-4 overflow-hidden">
            <div className="flex h-full">
              {(isAIMode ? aiLevels : levels).map((lvl, i) => {
                const pillar = getPillar(lvl.pillar?.primary || 'problem-solving');
                const segmentWidth = (1 / (isAIMode ? aiLevels : levels).length) * 100;
                const isCompleted = isAIMode ? completedAI[i] : completed[i];
                return (
                  <div
                    key={i}
                    className={`h-full transition-all duration-300 ${isCompleted ? pillar.progressColor : 'bg-slate-700'}`}
                    style={{ width: `${segmentWidth}%` }}
                    title={`Level ${i + 1}: ${lvl.title} (${pillar.shortName})`}
                  />
                );
              })}
            </div>
          </div>
          <p className="text-center mt-2">Progress: {progress}/{levels.length} levels completed</p>
        </div>
      </header>

      {/* Completion Ritual - Pause after success */}
      {showCompletionRitual && (
        <CompletionRitual
          levelNumber={level.id}
          levelTitle={level.title}
          conceptName={level.title.split(':')[1]?.trim() || level.title}
          teachBackPrompt={getStory(level.id)?.teachBackPrompt}
          lensCallout={level.lensCallout}
          onContinue={handleCompletionContinue}
        />
      )}

      {!showCompletionRitual && (
        showLearningCircle ? (
          <LearningCircle
            currentLevel={currentLevel}
            onClose={() => setShowLearningCircle(false)}
            onBackToSIFISO={handleBackToSIFISO}
          />
        ) : isPracticeMode ? (
          <div className="p-4">
            <PracticeMode
              onExit={() => setIsPracticeMode(false)}
              onBackToSIFISO={handleBackToSIFISO}
            />
          </div>
        ) : (
      <div className="flex flex-col md:flex-row gap-4 p-4">
        {/* Level Selector - Conditionally rendered */}
        {sidebarVisible && (
          <aside className="md:w-64 bg-slate-800 p-4 rounded-lg md:sticky md:top-4 md:max-h-[calc(100vh-2rem)] md:overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4 md:sticky md:top-0 md:bg-slate-800 md:pb-2 md:z-10">Levels</h2>
            <div className="grid grid-cols-3 md:grid-cols-1 gap-2">
              {(isAIMode ? aiLevels : levels).map((lvl, i) => {
                const pillar = getPillar(lvl.pillar?.primary || 'problem-solving');
                const levelCompleted = isAIMode ? completedAI[i] : completed[i];
                const isCurrentLevel = isAIMode ? currentAILevel === i : currentLevel === i;
                return (
                  <button
                    key={i}
                    onClick={() => isAIMode ? setCurrentAILevel(i) : setCurrentLevel(i)}
                    className={`p-3 rounded-lg text-center transition font-bold ${
                      levelCompleted
                        ? 'bg-green-700 text-white'
                        : isCurrentLevel
                        ? 'bg-blue-700 text-white'
                        : 'bg-slate-700 text-slate-300'
                    } hover:bg-blue-600 flex md:flex-col items-center justify-center gap-1 md:gap-2`}
                    title={`${lvl.title} - ${pillar.shortName}`}
                  >
                    <span className="text-lg md:text-xl">{pillar.icon}</span>
                    <div className="flex items-center gap-2 md:flex-col md:gap-0">
                      <span className="text-base md:text-lg">{i + 1}</span>
                      <span className="hidden md:block text-xs opacity-75">{pillar.shortName}</span>
                    </div>
                    {levelCompleted && <span className="text-sm md:text-base">✓</span>}
                  </button>
                );
              })}
            </div>
          </aside>
        )}

        {/* Main Content - Expands to full width when sidebar is hidden */}
        <main className="flex-1 flex flex-col gap-4">
          {/* Phase Indicator */}
          <div className="flex items-center justify-between bg-slate-800 p-4 rounded-lg">
            <div>
              <h2 className="text-2xl font-bold">Level {level.id}: {level.title}</h2>
              <p className="mt-2 text-slate-400">{level.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                (isAIMode ? currentAIPhase : currentPhase) === 'learning' ? 'bg-blue-600' : 'bg-slate-700'
              }`}>
                📚 Learn
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                (isAIMode ? currentAIPhase : currentPhase) === 'coding' ? 'bg-green-600' : 'bg-slate-700'
              }`}>
                💻 Code
              </span>
            </div>
          </div>

          {(isAIMode ? currentAIPhase : currentPhase) === 'learning' ? (
            <LearningView
              level={level}
              onStartCoding={handleStartCoding}
              onMarkComplete={handleMarkLevelComplete}
              onNextLevel={() => {
                if (isAIMode) {
                  if (currentAILevel < aiLevels.length - 1) {
                    setCurrentAILevel(currentAILevel + 1);
                  }
                } else {
                  if (currentLevel < levels.length - 1) {
                    setCurrentLevel(currentLevel + 1);
                  }
                }
              }}
              currentLevelIndex={isAIMode ? currentAILevel : currentLevel}
              totalLevels={isAIMode ? aiLevels.length : levels.length}
              story={isAIMode ? storiesAI[level.id] : getStory(level.id)}
            />
          ) : (
            <>
              {/* Back to Learning Button */}
              <button
                onClick={handleBackToLearning}
                className="self-start bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg text-sm"
              >
                ← Back to Learning
              </button>

          <section className="bg-slate-800 rounded-lg overflow-hidden">
            <Editor
              height="400px"
              defaultLanguage="python"
              theme="vs-dark"
              value={code}
              onChange={(value) => setCode(value || '')}
              options={{ fontSize: 16, minimap: { enabled: false } }}
            />
          </section>

          <div className="flex gap-4">
            <button onClick={handleRun} className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-semibold">
              Run Code
            </button>
            <button onClick={resetLevel} className="bg-slate-600 hover:bg-slate-700 px-6 py-3 rounded-lg">
              Reset
            </button>
            <button
              onClick={() => setShowHint(!showHint)}
              className="bg-purple-600 hover:bg-purple-700 px-6 py-3 rounded-lg"
            >
              {showHint ? 'Hide' : 'Show'} Hint
            </button>
          </div>

          {showHint && (
            <p className="bg-yellow-900 p-4 rounded-lg font-medium">
              Hint: {level.hint}
            </p>
          )}

          <section className="bg-slate-800 p-4 rounded-lg">
            <h3 className="text-xl font-bold">Output:</h3>
            <pre className="mt-2 bg-black p-4 rounded overflow-x-auto">
              {output || 'No output yet'}
            </pre>
            {feedback && <p className="mt-4 text-lg font-bold">{feedback}</p>}
          </section>

          {showExplanation && (
            <section className="bg-slate-800 p-4 rounded-lg">
              <h3 className="text-xl font-bold">Explanation:</h3>
              <p className="mt-2">{level.explanation}</p>
              {feedback.startsWith('❌') && level.tests.length > 0 && (
                <div className="mt-4">
                  <p className="font-semibold">Expected output:</p>
                  <pre className="bg-black p-4 rounded mt-2">
                    {typeof level.tests[0].expected === 'string'
                      ? level.tests[0].expected
                      : 'Pattern match'}
                  </pre>
                </div>
              )}
            </section>
          )}
            </>
          )}
        </main>
      </div>
      ))}
    </div>
  );
}

export default App;